/**
 * The dashboards are Arabic-first: `ar` is the default locale and `dir=rtl` is the default
 * direction. English is a full peer, not a fallback — every key exists in both, enforced by the
 * `Dictionary` type below and by a unit test.
 */

export const en = {
  'brand.name': 'Moqeem',
  'brand.tagline': 'Expo operations',

  'lang.toggle': 'العربية',
  'lang.label': 'Language',
  'theme.toggle': 'Theme',
  'theme.light': 'Light',
  'theme.dark': 'Dark',

  'nav.skip': 'Skip to main content',
  'nav.open': 'Open navigation',
  'nav.close': 'Close navigation',
  'nav.currentScope': 'Current access',
  'nav.signOut': 'Sign out',
  'nav.scope.organization': 'Organization',
  'nav.scope.role': 'Role',
  'nav.scope.surface': 'Surface',
  'nav.scope.assurance': 'Assurance',
  'nav.surface.dashboard': 'Management dashboard',
  'nav.surface.portal': 'Customer portal',
  'nav.assurance.standard': 'Standard access',
  'nav.assurance.elevated': 'Identity confirmed',
  'nav.environment': 'Environment: {env}. This is not production.',

  'portal.admin': 'Admin Panel',
  'portal.admin.desc': 'Expo lifecycle, floor plan, participations, releases, organizations, sponsors.',
  'portal.brokerage': 'Brokerage Portal',
  'portal.brokerage.desc': 'Invitations, campaign links, customers, assignment and reassignment.',
  'portal.developer': 'Developer Portal',
  'portal.developer.desc': 'Booth, branding, projects, inventory, offers, duty and the queue.',
  'portal.customer': 'Customer Portal',
  'portal.customer.desc': 'Enrolments, consents, who is serving me, sessions.',
  'portal.choose': 'Choose a portal',
  'portal.choose.hint':
    'The server decides what each portal may read. A portal you cannot use answers 404 for its records.',

  'auth.staff.title': 'Staff sign-in',
  'auth.staff.subtitle': 'Use your mobile number and password. OTP is available if you forgot it.',
  'auth.email': 'E-mail',
  'auth.password': 'Password',
  'auth.password.hint': 'Use at least 12 characters.',
  'auth.confirmPassword': 'Confirm password',
  'auth.passwordMismatch': 'The passwords do not match.',
  'auth.expoId': 'Expo ID',
  'auth.expoId.hint': 'The expo this customer is registering for.',
  'auth.ageConfirm': 'I confirm that I am at least 18 years old.',
  'auth.acceptTerms': 'I accept the terms and conditions.',
  'auth.acceptPrivacy': 'I accept the privacy notice.',
  'auth.readTerms': 'Read the terms and conditions',
  'auth.readPrivacy': 'Read the privacy notice',
  'auth.useOtp': 'Forgot password? Use OTP',
  'auth.usePassword': 'Use password instead',
  'auth.createAccount': 'Create a new account',
  'auth.totp': 'Authenticator code',
  'auth.signIn': 'Sign in',
  'auth.customer.title': 'Customer sign-in',
  'auth.customer.subtitle': 'Use your mobile number and password. OTP is optional if you forgot it.',
  'auth.phone': 'Phone number in E.164 form',
  'auth.captcha': 'Challenge token',
  'auth.captcha.hint': 'Supplied by the challenge widget when the server asks for one.',
  'auth.requestCode': 'Send the code',
  'auth.code': 'One-time code',
  'auth.verify': 'Verify',
  'auth.register': 'Complete registration',
  'auth.register.title': 'Complete your registration',
  'auth.register.hint': 'Your number is verified. Tell us your name to finish.',
  'auth.firstName': 'Full name',
  'auth.locale': 'Preferred language',
  'auth.resendIn': 'You may ask for another code in {seconds} s.',
  'auth.codeSent': 'A code was sent over {channel}.',
  'auth.back': 'Use a different number',
  'auth.signedOut': 'Signed out.',

  'stepUp.title': 'Elevated confirmation',
  'stepUp.body':
    'For your protection, confirm your identity again before completing this sensitive action. The confirmation is valid for five minutes and only for this action.',
  'stepUp.action': 'Action',
  'stepUp.confirm': 'Elevate',
  'stepUp.cancel': 'Cancel',
  'stepUp.active': 'Elevated until {time}',
  'stepUp.granted': 'Identity confirmed until {time}. This confirmation can only be used for the current action.',
  'stepUp.release': 'Cancel confirmation',

  'approval.title': 'Second approval (four eyes)',
  'approval.body':
    'This sensitive action needs approval from another authorized administrator before it can be completed.',
  'approval.action': 'Action',
  'approval.act': 'What is being approved',
  'approval.aggregate': 'Object',
  'approval.proposal': 'Proposed roles and scopes',
  'approval.expectedVersion': 'Record version',
  'approval.initiator': 'Initiator user id',
  'approval.initiator.hint':
    'The principal who will perform the act. You are approving it FOR them; the server refuses an approval whose initiator is you.',
  'approval.approver': 'approved by',
  'approval.reason': 'Reason',
  'approval.create': 'Record my approval',
  'approval.revoke': 'Withdraw my approval',
  'approval.hint':
    'You are recording YOUR approval of somebody else’s action. Sign in as the approver, record it here, then the initiator performs the operation.',
  'approval.handoff.hint':
    'Send the request below to a second holder of this permission. They open the approvals desk in THEIR OWN session, approve it there, and send back the approval. Never enter another person’s user id or credentials here.',
  'approval.request.label': 'Hand-off request (send this to the approver)',
  'approval.request.hint': 'It carries only the facts of the act. It is not a credential and grants nothing.',
  'approval.request.copy': 'Copy the request',
  'approval.request.incomplete':
    'The act is not fully specified yet — choose the object and let the screen read its current version.',
  'approval.grant.label': 'Approval from the second principal',
  'approval.grant.hint': 'Paste what the approver sent back. The approver’s identity comes from it, not from you.',
  'approval.grant.return': 'Send this back to the initiator.',
  'approval.grant.copy': 'Copy the approval',
  'approval.mismatch':
    'This approval was given for a different action, object or version. It cannot be spent here.',
  'approval.stale':
    'The act changed after this approval was accepted, so the approval no longer matches it and is not sent. Raise a new hand-off request for the act as it now stands.',
  'approval.desk.title': 'Approvals desk',
  'approval.desk.body':
    'You are the SECOND principal. What you record here is recorded under your own session, with your own step-up, and is held to every clause the act itself is held to.',
  'approval.desk.paste': 'Paste the hand-off request the initiator sent you.',
  'approval.takedown': 'Content this removes',
  'approval.release': 'Release this publishes',
  'approval.exception': 'Resolution this decides',
  'approval.intent':
    'Act digest (must be identical on both screens)',
  'details.technical': 'Technical details',
  // WHAT THIS REPLACES: 'approval.attested.title' / 'approval.attested', which said this act had no
  // approval record on the server and that naming an approver was an attestation rather than a
  // decision. That was true of the old route and is not true of this one — crm.ResolveException
  // consumes a real iam_approvals row — so the strings say what the server now does.
  'exception.deals.hint':
    'The deals whose commercial credit this correction moves, separated by commas. The order does not matter — both screens and the server sort them before binding.',
  'exception.resolve.title': 'Second approval (four eyes)',
  'exception.resolve.recorded':
    'A second Platform Super Admin records this decision in their own session, under their own step-up, bound to this exception at the version you read AND to the resolution below — the outcome, the corrected brokerage and broker, the expo, and the deals whose commercial credit moves. The server consumes that approval inside the resolution’s own transaction, re-checks the approver’s authority at that instant, and writes the approver it consumed onto the record. The raiser, the resolver and the approver are three distinct principals.',

  'conflict.title': 'Somebody else changed this record',
  'conflict.body':
    'The version this screen read is no longer current, so the request was refused rather than overwriting their change. Re-read the record and confirm the action again — this is not retried for you.',
  'conflict.reread': 'Re-read and start again',
  'conflict.decided': 'The server refused this because of the current state',
  'conflict.approvalPending': 'An approval for this act is already pending',
  'conflict.inProgress': 'A request with this key is still running',
  'conflict.keyReuse': 'This idempotency key was already used for a different request',
  'conflict.collect': 'Collect the outcome',

  'guard.audit': 'Before you continue',
  'guard.audit.body': 'This change will be saved in the activity log so it can be reviewed later.',
  'guard.typed.label': 'Type {value} to confirm',
  'guard.typed.mismatch': 'The typed value does not match.',
  'guard.reason': 'Reason',
  'guard.note': 'Note (optional)',

  'state.loading': 'Loading',
  'state.empty': 'Nothing here yet',
  'state.error': 'Could not complete the action',
  'state.error.body': 'The action could not be completed. Check the information and try again.',
  'state.diagnostics': 'Support details',
  'state.retry': 'Try again',
  'state.correlation': 'Correlation id',
  'state.copy': 'Copy',
  'state.copied': 'Copied',
  'state.unavailable': 'This surface is not served yet',
  'state.unavailable.body':
    'The backend answered {status} for {path}. Nothing is invented here: the screen stays empty until {package} lands.',
  'state.forbidden':
    'This record is outside your scope. The server answers 404 for a record you may not read, so "not found" and "not yours" look the same by contract.',
  'state.fields': 'Fields the server refused',
  'state.detail': 'Server detail (diagnostic)',

  'action.create': 'Create',
  'action.save': 'Save',
  'action.cancel': 'Cancel',
  'action.close': 'Close',
  'action.refresh': 'Refresh',
  'action.open': 'Open',
  'action.submit': 'Submit',
  'action.upload': 'Upload',
  'action.more': 'Load more',
  'action.filter': 'Filter',
  'action.all': 'All',

  'admin.expos.title': 'Expos',
  'admin.expos.desc':
    'Create and configure an expo, including its registration, start and end schedule.',
  'admin.expos.slug': 'Slug',
  'admin.expos.nameAr': 'Name (Arabic)',
  'admin.expos.nameEn': 'Name (English)',
  'admin.expos.timezone': 'Timezone',
  'admin.expos.expectedBooths': 'Expected booth count',
  'admin.expos.expectedBoothsHint': 'Planning estimate only. Actual assigned BoothDesign footprints determine the hall.',
  'admin.expos.boothCounts': 'Actual / expected booths',
  'admin.expos.status': 'Status',
  'admin.expos.version': 'Version',
  'admin.expos.createHint': 'Add the essential details now. Sessions, floor plan and participants come next.',
  'admin.expos.listHint': 'Open an expo to manage its lifecycle, sessions, floor, participation and releases.',
  'admin.expos.empty': 'No expo exists yet. Create the first one to open the floor plan and participations.',
  'admin.expo.lifecycle': 'Lifecycle',
  'admin.expo.lifecycle.desc':
    'Move the expo through announcement, registration, live operation and completion. The system prevents invalid moves automatically.',
  'admin.expo.schedule.title': 'Event schedule',
  'admin.expo.schedule.createHint': 'Set the public dates now; you can edit them later from the event page.',
  'admin.expo.schedule.editHint': 'These dates drive the public lifecycle and remain editable until the related stage has passed.',
  'admin.expo.schedule.registration': 'Registration opens',
  'admin.expo.schedule.start': 'Event starts',
  'admin.expo.schedule.end': 'Event ends',
  'admin.expo.schedule.timezone': 'Event timezone',
  'admin.expo.schedule.timezoneHint': 'Entered in {timezone}; it is not interpreted using the browser timezone.',
  'admin.expo.schedule.startHint': 'Registration closes at this time, then the event goes live when the hall release is ready.',
  'admin.expo.schedule.automatic': 'Change statuses automatically at these times',
  'admin.expo.schedule.automaticHint':
    'The server opens registration, closes it at the event start, goes live when the hall is ready, and ends the event at its end time.',
  'admin.expo.schedule.error.required': 'This date is required.',
  'admin.expo.schedule.error.startOrder': 'The event must start after registration opens.',
  'admin.expo.schedule.error.endOrder': 'The event must end after it starts.',
  'admin.expo.schedule.error.timezone': 'This time is not valid in the selected event timezone.',
  'admin.expo.schedule.error.summary': 'Review the highlighted event dates before saving.',
  'admin.expo.transition': 'Change status',
  'admin.expo.transition.notice':
    'The expo status will change to “{status}”. If someone else changes it first, you will be asked to refresh instead of losing their work.',
  'admin.expo.transition.notReady': 'This stage is not ready yet',
  'admin.expo.transition.registrationRemedy':
    'Set the registration opening date in the event schedule, or wait until that date. The server will open it automatically when automation is enabled.',
  'admin.expo.transition.liveRemedy':
    'The event start time must have arrived and at least one session must carry an active public release. The server retries automatically when the hall becomes ready.',
  'admin.expo.transition.scheduleRemedy': 'Check the event schedule and the readiness requirements shown on this page.',
  'admin.expo.sessions': 'Session windows',
  'admin.expo.sessions.desc':
    'Non-overlapping windows in the expo timezone. Overlap is refused by an exclusion constraint, with the conflicting session shown.',
  'admin.expo.sessions.starts': 'Starts at',
  'admin.expo.sessions.ends': 'Ends at',
  'admin.expo.sla': 'Team response settings',
  'admin.expo.sla.obligation':
    '100 % staffing of every entitled seat is the obligation. It is not a field on this screen: it cannot be lowered, waived or scheduled away, and a recorded breach cannot be edited or deleted from here or anywhere else.',
  'admin.expo.recordingPolicy': 'Recording policy',
  'admin.expo.recordingPolicy.desc':
    'Choose whether session recording is disabled or attempted when available. Changes require identity confirmation and approval from another administrator.',
  'admin.expo.recordingPolicy.notice':
    'The recording setting will change to “{policy}”. The reason for the change will be kept in the activity log.',
  'expo.status.draft': 'Draft',
  'expo.status.announced': 'Announced',
  'expo.status.registration_open': 'Registration open',
  'expo.status.registration_closed': 'Registration closed',
  'expo.status.live': 'Live',
  'expo.status.ended': 'Ended',
  'expo.status.archived': 'Archived',
  'expo.status.cancelled': 'Cancelled',
  'reason.operational': 'Operational need',
  'reason.commercial': 'Commercial decision',
  'reason.compliance': 'Compliance requirement',
  'reason.security': 'Security requirement',
  'reason.administrative_error': 'Correct an administrative error',
  'recording.disabled': 'Disabled',
  'recording.best_effort': 'Record when available',
  'recording.required': 'Required recording',
  'recording.noticeVersion': 'Recording notice version',
  'recording.justification': 'Reason for change',
  'sla.callAcceptTimeout': 'Time allowed to answer a call (seconds)',
  'sla.missedCallLimit': 'Missed-call limit before an alert (%)',
  'sla.unstaffedGrace': 'Grace period without staff (seconds)',
  'sla.seatHoldDuration': 'Temporary seat reservation (seconds)',
  'sla.queueOfferWindow': 'Time to accept a queue request (seconds)',
  'sla.queueReofferLimit': 'Maximum queue reoffers',
  'sla.closeGrace': 'Grace period after closing (seconds)',
  'admin.expo.recordingPolicy.gated.short': 'Unavailable until legal review is complete',
  'admin.expo.recordingPolicy.gated':
    'Required recording will become available after legal and privacy approval. Until then, choose “Disabled” or “Record when available”.',
  'admin.expo.slots': 'Booth slots',
  'admin.expo.participations': 'Participations',
  'admin.expo.sponsors': 'Expo sponsors',
  'admin.expo.releases': 'Releases',
  'admin.expo.revenue': 'Entitlements and revenue items',

  'admin.layouts.title': 'Hall layouts',
  'admin.layouts.desc':
    'The catalogue of hall layouts and their slot definitions. A published layout is immutable; a change creates a new version.',
  'admin.layouts.publishedAt': 'Published on',
  'admin.layouts.readOnly':
    'The register is read-only. A hall layout is generated by the server from this expo’s own participations and published from the floor console; there is nothing to compose by hand.',

  'admin.currentHall.title': 'Current hall',
  'admin.currentHall.activeTitle': 'Active hall',
  'admin.currentHall.desc':
    'There is exactly one hall. The client accepts this scene key at this scene version and refuses anything else before it loads a single asset.',
  'admin.currentHall.sceneKey': 'Scene key',
  'admin.currentHall.sceneVersion': 'Scene version',
  'admin.currentHall.hallId': 'Hall identifier',
  'admin.currentHall.layoutVersion': 'Layout version',
  'admin.currentHall.active': 'Active on this expo',
  'admin.currentHall.inactive': 'Published but not active',
  'admin.currentHall.inactiveHint':
    'A layout is published for this hall, but this expo is not attached to it: visitors would arrive at an empty world. Publish the plan again from the floor console to attach it.',
  'admin.currentHall.none': 'No hall published yet',
  'admin.currentHall.noneHint':
    'Generate a plan and publish it from the floor console below. Until then this expo has no world to enter.',
  'admin.currentHall.publishedLayout': 'Published layout v{version}',
  'admin.currentHall.openFloor': 'Open the floor console',
  'admin.templates.title': 'BoothDesign catalogue',
  'admin.templates.size': 'Booth size',
  'admin.templates.dimensions': 'Measured dimensions',
  'admin.templates.seatUnit': 'seats',
  'admin.templates.maxSeats': 'Maximum seats',
  'admin.templates.brandingSlots': 'Branding areas',
  'admin.slots.title': 'Floor plan',
  'admin.slots.desc':
    'Generate and publish the authoritative floor plan from actual assigned BoothDesigns. The planning estimate never limits capacity.',
  'admin.layoutEngine.title': 'Procedural expo layout',
  'admin.layoutEngine.desc': 'The server scores a deterministic modular plan from purchased booth footprints, circulation clearances, tiers and pins.',
  'admin.layoutEngine.counts': 'Booth planning counts',
  'admin.layoutEngine.expected': 'Planning estimate',
  'admin.layoutEngine.actual': 'Actual assigned',
  'admin.layoutEngine.status': 'Artifact status',
  'admin.layoutEngine.version': 'Layout version',
  'admin.layoutEngine.stale': 'This draft is stale because a participation, BoothDesign or planning input changed. Generate a new draft before publishing.',
  'admin.layoutEngine.ceiling': 'Clear ceiling height (m)',
  'admin.layoutEngine.aisle': 'Main aisle width (m)',
  'admin.layoutEngine.bay': 'Architectural bay depth (m)',
  'admin.layoutEngine.run': 'Target maximum avenue run (m)',
  'admin.layoutEngine.avenues': 'Maximum parallel avenues',
  'admin.layoutEngine.generate': 'Generate new draft',
  'admin.layoutEngine.publish': 'Publish this exact draft',
  'admin.layoutEngine.preview': 'Authoritative plan preview',
  'admin.layoutEngine.previewHint': 'This renders the persisted server snapshot that Unity consumes; it does not run a browser-side solver.',
  'admin.layoutEngine.empty': 'Assign BoothDesigns to accepted participations, then generate a draft.',
  'admin.layoutEngine.planAlt': 'Top-down authoritative expo layout with aisles and booth footprints',
  'admin.layoutEngine.avenueUnit': 'avenues',
  'admin.layoutEngine.publishedSlots': 'Published runtime slots',
  'admin.layoutEngine.tier': 'Placement tier',
  'admin.layoutEngine.minWidth': 'Minimum hall width (m)',
  'admin.layoutEngine.minDepth': 'Minimum hall depth (m)',
  'admin.layoutEngine.crossAisle': 'Cross aisle width (m)',
  'admin.layoutEngine.margin': 'Perimeter margin (m)',
  'admin.layoutEngine.entrance': 'Entrance zone depth (m)',
  'admin.layoutEngine.endFeature': 'End feature depth (m)',
  'admin.layoutEngine.aspect': 'Target aspect ratio',
  'admin.layoutEngine.validation': 'Plan validation',
  'admin.layoutEngine.validationClean': 'The solver reported no errors and no warnings for this plan.',
  'admin.participations.developer': 'Developer',
  'admin.participations.design': 'BoothDesign',
  'admin.participations.chooseDeveloper': 'Choose a developer',
  'admin.participations.chooseDesign': 'Choose a purchased booth design',
  'admin.participations.tier': 'Commercial placement tier',
  'admin.participations.seats': 'Purchased seats',
  'admin.participations.saveDesign': 'Save BoothDesign assignment',
  'admin.participations.slot': 'Booth slot',
  'admin.participations.publishBlocked':
    'The server does not consider this booth publishable yet. The readiness panel names every check it evaluated.',
  'admin.participations.suspendConfirm': 'Type the participation id to enable Suspend',
  'admin.participations.suspendHint':
    'Suspending takes a live booth out of the world for everyone standing in front of it, so the identifier is typed first.',
  'admin.verb.accept': 'Accept',
  'admin.verb.withdraw': 'Withdraw',
  'admin.verb.publish': 'Publish',
  'admin.verb.republish': 'Republish',
  'admin.verb.suspend': 'Suspend',
  'admin.verb.take_down': 'Take down',
  'admin.branding.approve': 'Approve',
  'admin.branding.reject': 'Reject',
  'admin.branding.rejectReason': 'Rejection reason code',
  'admin.branding.previewAlt': 'Branding asset submitted as {kind}',
  'media.preview.unavailable':
    'This preview could not be loaded. The asset may not be an image the server will render, or the request failed.',

  'readiness.title': 'Publication readiness',
  'readiness.desc':
    'Computed by the server from the same rules the publish transition enforces. Nothing here is inferred by this screen.',
  'readiness.check': 'Check',
  'readiness.requirement': 'Requirement',
  'readiness.detail': 'Server detail',
  'readiness.required': 'Required',
  'readiness.optional': 'Optional',
  'readiness.publishable': 'Ready to publish',
  'readiness.blocked': 'Not ready to publish',
  'readiness.state.ok': 'Met',
  'readiness.state.pending': 'Awaiting review',
  'readiness.state.missing': 'Missing',
  'readiness.state.rejected': 'Rejected',
  'readiness.reason': 'Reason code',
  'readiness.check.organization_active': 'Organization active',
  'readiness.check.template_assigned': 'BoothDesign assigned',
  'readiness.check.slot_assigned': 'Booth slot assigned',
  'readiness.check.layout_published': 'Hall layout published',
  'readiness.check.branding_logo': 'Logo approved',
  'readiness.check.branding_hero_banner': 'Hero banner approved',
  'readiness.check.branding_screen_video': 'Screen video approved',
  'readiness.check.seats_issued': 'Seats issued',
  'admin.tier.standard': 'Standard',
  'admin.tier.premium': 'Premium',
  'admin.tier.signature': 'Signature',
  'admin.tier.flagship': 'Flagship',
  'admin.participations.desc':
    'One participation per developer per expo and one per slot, both by unique constraint. Entitlements are informational records; contracts and invoicing stay outside the system.',
  'admin.branding.title': 'Branding review',
  'admin.branding.desc':
    'Approval advances the construction stage to BRANDED. That consequence is stated before the click.',
  'admin.releases.desc':
    'Build an immutable release from a validated snapshot. A build finding is projection, path and rule; a failed build is not publishable and there is no runtime filter to fall back on.',
  'admin.releases.activate.desc':
    'Activation and rollback both need step-up AND a four-eyes approval. Approver equal to initiator is refused — and this screen says so before the attempt.',
  'admin.takedown.title': 'Booth takedown',
  'admin.takedown.desc':
    'Takedown is terminal for this expo: it purges the edge, denies at the origin, revokes release projections, cancels open queue entries and ends active meetings at that booth. Step-up, four eyes, a reason code and a legal reference.',
  'admin.orgs.title': 'Organizations',
  'admin.orgs.desc':
    'Brokerage, developer and platform organizations, their memberships and staff verification badges.',
  'admin.memberships.title': 'Memberships and roles',
  'admin.memberships.desc':
    'Role assignment is a compare-and-swap: the membership version you read is sent as If-Match, so a concurrent change is a conflict rather than an overwrite.',
  'admin.sponsors.desc':
    'Sponsorship grants no booth, no seat, no in-world placement and no customer data. The public projection returns only active sponsors inside their period, ordered by tier rank then display order; expiry is computed server-side.',
  'admin.exceptions.title': 'Attribution exceptions',
  'admin.exceptions.desc':
    'There is no dispute process and no arbitration between two brokerages here. There is no SLA clock, no dispute window and no appeal stage.',
  'admin.customers.title': 'Customers',
  // BE-4 REVERSES ROUND-3 #15. The reveal route now REQUIRES `purpose` and `case_reference` as
  // query parameters and writes both into the `customer.pii.revealed` row's detail (with the case
  // reference also in `aud_events.reason`), so the header states them as recorded again — which is
  // what doc 04 ADM-14 asks the screen to say.
  'admin.customers.desc':
    'The masked register. Unmasked fields need a step-up raised for customer.pii.reveal plus a purpose and a case reference, which the server requires and records against the disclosure. Export is a separate permission.',
  'admin.leads.title': 'Direct / house leads',
  'admin.leads.desc':
    'Every direct customer with no brokerage-level owner for the expo. Time-unrouted is the primary sort, because an unserved house lead is the failure this screen exists to prevent.',
  'admin.identity.title': 'Identity merge and split',
  'admin.identity.desc':
    'A merge never rewrites acquisition history: aliases and resolution history are created instead. Step-up and four eyes on both operations.',
  'admin.approvals.title': 'Approvals',
  'admin.approvals.desc':
    'The second principal’s desk. Open it in YOUR OWN session: what you record here is your approval of somebody else’s act, under your own step-up.',
  'reveal.title': 'Reveal contact details',
  // BE-4: the purpose and the case reference are REQUIRED by the route and recorded by it — this
  // line says so, rather than describing them as something the screen merely asks for.
  // BE-8: and it is an ACTION, not a read. The act spends a one-use grant and writes a disclosure
  // record, so it is a POST that carries its own idempotency key — which is what lets a lost answer
  // be collected instead of paid for a second time.
  'reveal.desc':
    'A reveal is a separate, audited action. It needs a step-up raised for customer.pii.reveal, and the server requires a purpose and a case reference and records both against the disclosure. It spends the step-up: one grant, one person. If the answer is lost, asking again under the same request key returns that same one reveal rather than performing another.',
  // THE STANDING STATEMENT ABOVE THE FIELDS, in both languages (P13-m).
  //
  // Round-5 A4(d) corrected it — it called the act "a separate, step-up-gated read" and then said
  // "that read also SPENDS the step-up grant", a sentence arguing with its own noun — and left the
  // correction as hardcoded English JSX on an Arabic-FIRST surface. The one paragraph that exists to
  // stop an operator believing they are looking something up was unreadable to the operators these
  // dashboards default to.
  //
  // It is two keys because it makes two claims: what the act records, and what performing it costs.
  // The permission codes stay inline, as they do in every sibling key here — they are identifiers
  // the operator matches against the server's own vocabulary, not words to translate.
  'reveal.statement':
    'customer.pii.reveal — a separate, step-up-gated action, not a lookup. The server writes one audit row, customer.pii.revealed, naming the ACTOR and the CUSTOMER, in the same transaction as the decrypt — and the purpose and case reference below go INTO that row: both in its detail, the case reference also in its reason, beside the record shape they were written under. The route requires them.',
  'reveal.statement.spends':
    'Performing it also SPENDS the step-up grant that authorised it, so unmasking the next person needs a new factor. Export is a DIFFERENT permission, with row and frequency limits.',
  'reveal.purpose': 'Purpose',
  'reveal.case': 'Case reference',
  'reveal.run': 'Reveal',
  'reveal.audited':
    'The server recorded customer.pii.revealed for {customer}, naming you as the actor, in the same transaction that decrypted the number — with your purpose and case reference in the row’s detail, the case reference also in its reason, and the record shape it was written under. It spent the step-up grant that authorised it.',
  'reveal.audited.badge': 'server audit written',
  'reveal.audited.nothing': 'nothing revealed, so no server audit',
  'reveal.audited.none': 'no server audit (the reveal was refused)',
  'reveal.audited.unavailable': 'nothing to reveal, so no server audit',
  // ROUND-4 A4, WIDENED IN ROUND-5: the attempt whose answer never arrived OR came back a 5xx.
  // "Refused" would be a false entry in the one record this screen owns — the request may have
  // committed the disclosure in either case.
  'reveal.audited.unknown': 'outcome unknown — this tab has no answer it can trust',
  // P13-n: NEITHER OF THE TWO ENTRIES AROUND IT. `409 idempotency.in_progress` is not a refusal —
  // nothing was refused — and it is not the unknown either: the server said something exact, which
  // is that the attempt under this key has not finished. Borrowing either wording would write a
  // false line into the one record this screen owns.
  'reveal.audited.running': 'still being processed — the first attempt has not finished',
  // SINCE BE-6 THIS IS OFF-CONTRACT, and the string says so rather than repeating the explanation
  // that now belongs to `reveal.unavailable`. A record the CRM holds no number for is a typed 422;
  // a 200 carrying no decrypted field is a server this screen does not recognise, so the safe thing
  // is to claim nothing — least of all an audit row — and say the answer was unexpected.
  'reveal.nothing': 'Nothing was revealed',
  'reveal.nothing.body':
    'The server answered 200 and returned no decrypted field. That is not an answer this route is expected to give — a record whose number the CRM does not hold is refused with customer.reveal_unavailable — so nothing is claimed here: no disclosure was shown, and no customer.pii.revealed row is assumed to have been written.',
  // BE-6: `customer.reveal_unavailable` is its OWN outcome, and it is not a fault. The record
  // carries another context's envelope, so there is no number here to unmask; no step-up was spent
  // and no disclosure row was written. It is deliberately not in `isRetryableRefusal` — a fresh
  // factor cannot help — so this state offers no retry and no remedy.
  'reveal.unavailable': 'The CRM does not hold this number',
  'reveal.unavailable.body':
    'This record’s phone was captured by another context and is sealed under that context’s key, so there is nothing here for this screen to unmask. Your step-up grant was NOT spent and no disclosure was recorded. Collecting another factor will not change the answer — the next time this person registers, the CRM writes its own copy and the reveal becomes possible.',
  // ROUND-4 A4: a lost connection is not a refusal, and the difference is the operator's factor.
  // ROUND-5 A4: neither is a 5xx, so the title no longer claims that nothing arrived — something may
  // well have arrived and still said nothing about what the server did.
  'reveal.unknown': 'The outcome is not known here',
  'reveal.unknown.body':
    'Either nothing came back at all or the server answered with a fault, and neither says what became of the request: the reveal may already have been recorded, or it may never have reached the point that records it. Your step-up grant is kept — this tab did not spend it, and the server spends it only inside the transaction that decrypts and audits.',
  // ROUND-5 A4(c): why the ordinary control is disabled, said where the operator is looking.
  'reveal.unknown.blocked':
    'Until that is settled this panel starts nothing new: a fresh reveal would carry a new request key, and only the original key can ask the server what it did. Try again to ask under that key — a reveal that was recorded is replayed to you exactly as it was recorded, one disclosure and one audit row, and one that never happened is performed now.',
  'reveal.unknown.abandon': 'Abandon this attempt',
  'reveal.unknown.abandon.warning':
    'Abandoning drops this tab’s only handle on that attempt: it can never be replayed from here. So if it did commit, the disclosure and its customer.pii.revealed row stand on the server with your name on them, and the grant was spent on it — the next reveal will be refused until you raise a new factor.',
  // P13-n — THE OTHER OPEN ATTEMPT, AND IT IS NOT AN UNKNOWN. `409 idempotency.in_progress` is the
  // server saying that the request under this key is executing right now. The panel is held for the
  // same reasons and says so in its own words: an operator told "the outcome is not known here"
  // about an act the server has just described would be told something false.
  'reveal.running': 'The first attempt is still running',
  'reveal.running.body':
    'The server answered that a request with this key is still executing: the reveal you asked for has not finished, so nothing has been refused and nothing has been decided — it may be being recorded as you read this. Your step-up grant is kept: this tab did not spend it, and the server spends it only inside the transaction that decrypts and audits.',
  'reveal.running.blocked':
    'Until it finishes this panel starts nothing new: a fresh reveal would carry a new request key and would be a SECOND disclosure of this person, alongside the one already running. Collect the outcome again in a moment — when the first attempt lands, that same key hands back exactly what it recorded, one disclosure and one audit row.',
  'reveal.running.abandon.warning':
    'Abandoning drops this tab’s only handle on an attempt the server is running right now: it can never be collected from here. It will finish without you — and if it discloses, the disclosure and its customer.pii.revealed row stand on the server with your name on them, and the grant was spent on it.',
  'reveal.trail': 'This session’s local record',
  'reveal.trail.local':
    'This branch serves no audit-trail read route, so what follows is THIS TAB’s own list of the reveals it asked for. It is not the server’s audit trail, it is not sent anywhere, and it is gone when the tab closes. The server-side trail this screen can show is the attribution timeline below.',
  'reveal.none': 'Nothing has been revealed in this session.',

  'brk.invitations.title': 'Invitations',
  'brk.invitations.desc':
    'Unique customer invitations through pending → sent → opened → accepted, plus expired, revoked and conflicted. A send failure leaves the invitation pending and retryable.',
  'brk.links.title': 'Campaign links',
  'brk.links.desc': 'Registration links with channel and UTM parameters. A revoked link is read-only.',
  'brk.leads.title': 'Leads',
  'brk.leads.desc':
    'Identity stays masked until assignment. An open attribution exception freezes nothing and never blocks an assignment.',
  'brk.unassigned': 'Unassigned only',
  'brk.pending.title': 'Pending reassignment',
  'brk.pending.desc':
    'These customers’ broker left, was deactivated, suspended or deleted. The brokerage still owns and still serves them; only the broker-level assignment is open. The queue has no timer and no expiry.',
  'brk.pending.empty': 'No customers are waiting for a broker. That is the healthy state.',
  'brk.bulk.title': 'Bulk reassign',
  'brk.bulk.desc':
    'Successes commit and failures are listed individually with their reason; nothing is retried silently. All rows share one bulk operation id so the operation can be reviewed as a set.',
  'brk.customer.title': 'Customer',
  'brk.timeline.title': 'Attribution timeline',
  'brk.timeline.desc':
    'A carry-forward row is an indefinite broker-level relationship with no recency window. A later touch from another brokerage is recorded as evidence with no effect on ownership.',
  'brk.notes.title': 'Notes',
  // ROUND-4 A3/B1. THE ATTESTATION IS GONE, AND SO IS THE COPY THAT DESCRIBED ONE (BE-3). This line
  // said resolution was "a named attestation — the act creates and consumes no approval record, so
  // it is not four eyes", which was true of the route it was written against: the resolver typed a
  // colleague's user id and the server checked only that that principal WOULD hold the permission.
  // `decodeStrict` now refuses `approver_user_id` outright. The second actor is the approver of an
  // `iam_approvals` row raised from their OWN session and consumed inside the resolution's
  // transaction, and `ck_crm_attribution_exceptions_four_eyes` enforces the pairing in the schema.
  // Telling a brokerage that the decision on their exception is unwitnessed is now the false claim.
  'brk.exceptions.desc':
    'The system record is authoritative. Ordinary claims over a customer are not a category here: only administrative_error, identity_merge_split, fraud and data_integrity. Raising is Brokerage Admin only; resolution is a Platform Super Admin action.',
  'brk.exceptions.empty': 'This brokerage has raised no attribution exception.',
  'brk.exceptions.resolution':
    'How your exception is decided: a Platform Super Admin resolves it under a step-up raised for that action, spending a one-use approval that a SECOND administrator recorded from their own session against this exception at the version it was read at. The resolution consumes that approval inside the same transaction that changes the record, and stores the approver it named — so the decision is four eyes, and the trail shows both principals rather than a name typed by the resolver.',

  'dev.booth.title': 'Booth',
  'dev.booth.desc':
    'Slot, template, seat count, accent colour and kiosk layout, within the entitlement. Slot and template changes are platform actions; the developer requests them.',
  'dev.branding.title': 'Branding',
  'dev.branding.desc':
    'Branding upload advances the construction stage. With no assets the booth renders at SCAFFOLD/STRUCTURE. Approval is a platform action.',
  'dev.branding.quota': 'Entitlement: {used} of {limit} assets used for this participation.',
  'dev.branding.kind.logo': 'Logo',
  'dev.branding.kind.hero_banner': 'Hero banner',
  'dev.branding.kind.screen_video': 'Screen video',
  'dev.branding.none': 'Nothing uploaded for this slot yet.',
  'dev.branding.locale': 'Language of this asset',
  'dev.branding.file': 'Choose a file',
  'dev.branding.noFile': 'Choose a file before uploading.',
  'dev.branding.uploaded': 'Uploaded and submitted for review in one step.',
  'dev.branding.previewAlt': 'Uploaded {kind}',
  'dev.branding.noStill': 'A video has no still preview here; the reviewer plays the file itself.',
  'dev.branding.atQuota': 'This booth has used every branding slot its BoothDesign allows.',
  'dev.booth.noTemplate': 'No BoothDesign assigned',
  'dev.booth.noTemplateHint':
    'The expo team has not assigned a BoothDesign to this participation yet, so its seat and branding entitlements are not decided.',
  'dev.readiness.desc':
    'What the expo team’s publication check sees. You cannot publish your own booth; this is what is still missing.',
  'dev.company.title': 'Company profile',
  'dev.company.desc':
    'Your organization as visitors and the expo team see it. The legal identity and the verification state are the platform’s to decide; the rest is yours to write.',
  'dev.company.identity': 'Legal identity',
  'dev.company.identityHint': 'Read-only. Ask the expo team to correct anything wrong here.',
  'dev.company.legalNameAr': 'Legal name (Arabic)',
  'dev.company.legalNameEn': 'Legal name (English)',
  'dev.company.displayName': 'Display name',
  'dev.company.verification': 'Verification state',
  'dev.company.country': 'Country',
  'dev.company.profile': 'Public profile',
  'dev.company.profileHint':
    'Saved as a compare-and-swap against the version read above, so a colleague editing at the same time is a conflict rather than a silent overwrite.',
  'dev.company.website': 'Website',
  'dev.company.websiteHint': 'An https address, or empty.',
  'dev.company.websiteError': 'The website must be a complete https address, or empty.',
  'dev.company.aboutAr': 'About (Arabic)',
  'dev.company.aboutEn': 'About (English)',
  'dev.company.aboutError': 'At most 2000 characters.',
  'dev.company.saved': 'Profile saved.',
  'dev.projects.title': 'Projects',
  'dev.projects.desc': 'An approved project is read-only except through a new submission.',
  'dev.unitTypes.title': 'Unit types',
  'dev.units.import': 'Import units',
  'dev.units.import.desc':
    'CSV or JSON. Every row is reported individually: the batch is never wholly rejected for one bad row.',
  'dev.price.title': 'List price',
  'dev.offers.title': 'Offers',
  'dev.offers.desc':
    'Scope, kind, value, validity, quota and publish tier. Publishing a definition is not a claim authority.',
  'dev.holds.title': 'Holds',
  'dev.holds.desc':
    'A hold is created against the developer’s own TTL and snapshotted; a later policy change never moves an existing hold. An expired hold is never silently re-held.',
  'dev.holds.conflict':
    'The hold this screen is holding is stale — it was extended, released or expired somewhere else. This screen cannot re-read it for you: the developer surface serves POST create, extend and release and no read route at all, so nothing here can show you its current state. Clear it below and create a new hold if the unit is still free.',
  'dev.holds.forget': 'Clear this stale hold from the screen',
  'dev.holdPolicy.title': 'Hold policy',
  'dev.holdPolicy.desc':
    'Hold TTL in hours and the permitted extensions. The record always exists — created with defaults at onboarding — so there is no "not configured" state. A change applies only to holds created afterwards.',
  'dev.announce.title': 'Announce projects',
  'dev.announce.desc': 'Which projects appear on the booth, and in what order. Requires at least one published project.',
  'dev.duty.title': 'Duty console',
  'dev.duty.desc':
    'Check-out with no incoming representative is available and presented for what it is: from that moment the seat is unstaffed and an SLA breach interval is open.',
  'dev.duty.checkIn': 'Check in',
  'dev.duty.handover': 'Hand over',
  'dev.duty.checkOut': 'Check out',
  'dev.duty.offDuty': 'You are off duty.',
  'dev.queue.title': 'Queue monitor',
  'dev.queue.desc':
    'Ordering is booked slot, then VIP, then walk-up FIFO, supplied by the server. The priority class is shown; the rank is never editable.',
  'dev.queue.callNext': 'Call next',
  'dev.queue.empty': 'No one is waiting.',
  'dev.queue.noSeat':
    'You hold no on-duty seat, so the queue is visible and no call control is offered.',

  'cus.expos.title': 'Expos',
  'cus.enroll': 'Register for this expo',
  'cus.enrollments.title': 'My registrations',
  'cus.consents.title': 'Privacy centre',
  'cus.consents.desc':
    'Consent decisions with their notice versions. Withdrawing analytics consent suppresses scores and alerts immediately, before deletion jobs run.',
  'cus.attribution.title': 'Who is serving me',
  'cus.attribution.desc':
    'The relationship carries forward from expo to expo for as long as the broker is active at that brokerage. There is no dispute process for you to enter.',
  'cus.attribution.none':
    'You are served directly by our floor team.',
  'cus.blocks.title': 'Blocked contacts',
  'cus.sessions.title': 'Sessions and devices',
  'cus.sessions.desc': 'Revoking the current session signs you out. That is stated first.',
  'cus.sessions.revokeOthers': 'Revoke all other sessions',
  'cus.shortlist.title': 'Shortlist',

  'table.identifier': 'Identifier',
  'table.created': 'Created',
  'table.status': 'Status',
  'table.actions': 'Actions',
  'table.name': 'Name',
  'table.kind': 'Kind',
  'table.code': 'Code',
  'table.rank': 'Rank',
  'table.none': '—',
  'status.active': 'Active',
  'status.retired': 'Retired',
  'status.draft': 'Draft',
  'status.published': 'Published',
  'action.publish': 'Publish',
} as const;

export type StringKey = keyof typeof en;
export type Dictionary = Record<StringKey, string>;

export const ar: Dictionary = {
  'brand.name': 'مقيم',
  'brand.tagline': 'تشغيل المعارض',

  'lang.toggle': 'English',
  'lang.label': 'اللغة',
  'theme.toggle': 'المظهر',
  'theme.light': 'فاتح',
  'theme.dark': 'داكن',

  'nav.skip': 'تخطَّ إلى المحتوى',
  'nav.open': 'فتح التنقل',
  'nav.close': 'إغلاق التنقل',
  'nav.currentScope': 'نطاق الوصول الحالي',
  'nav.signOut': 'تسجيل الخروج',
  'nav.scope.organization': 'المنشأة',
  'nav.scope.role': 'الدور',
  'nav.scope.surface': 'الواجهة',
  'nav.scope.assurance': 'مستوى التحقق',
  'nav.surface.dashboard': 'لوحة الإدارة',
  'nav.surface.portal': 'بوابة العملاء',
  'nav.assurance.standard': 'وصول عادي',
  'nav.assurance.elevated': 'تم تأكيد الهوية',
  'nav.environment': 'البيئة: {env}. هذه ليست بيئة الإنتاج.',

  'portal.admin': 'لوحة الإدارة',
  'portal.admin.desc': 'دورة حياة المعرض، مخطط الأرضية، المشاركات، الإصدارات، المنشآت، الرعاة.',
  'portal.brokerage': 'بوابة الوساطة',
  'portal.brokerage.desc': 'الدعوات، روابط الحملات، العملاء، الإسناد وإعادة الإسناد.',
  'portal.developer': 'بوابة المطوّر',
  'portal.developer.desc': 'الجناح، الهوية البصرية، المشاريع، المخزون، العروض، المناوبة والطابور.',
  'portal.customer': 'بوابة العميل',
  'portal.customer.desc': 'التسجيلات، الموافقات، من يخدمني، الجلسات.',
  'portal.choose': 'اختر البوابة',
  'portal.choose.hint':
    'الخادم هو من يقرّر ما تستطيع كل بوابة قراءته. البوابة التي لا تملك صلاحيتها تُجيب ٤٠٤ عن سجلاتها.',

  'auth.staff.title': 'دخول الموظفين',
  'auth.staff.subtitle': 'استخدم رقم الجوال وكلمة المرور، ويمكن الدخول برمز تحقق عند نسيانها.',
  'auth.email': 'البريد الإلكتروني',
  'auth.password': 'كلمة المرور',
  'auth.password.hint': 'استخدم ١٢ حرفًا على الأقل.',
  'auth.confirmPassword': 'تأكيد كلمة المرور',
  'auth.passwordMismatch': 'كلمتا المرور غير متطابقتين.',
  'auth.expoId': 'معرّف المعرض',
  'auth.expoId.hint': 'المعرض الذي يُسجّل فيه العميل.',
  'auth.ageConfirm': 'أُقرّ بأن عمري ١٨ سنة فأكثر.',
  'auth.acceptTerms': 'أوافق على الشروط والأحكام.',
  'auth.acceptPrivacy': 'أوافق على إشعار الخصوصية.',
  'auth.readTerms': 'قراءة الشروط والأحكام',
  'auth.readPrivacy': 'قراءة إشعار الخصوصية',
  'auth.useOtp': 'نسيت كلمة المرور؟ استخدم رمز تحقق',
  'auth.usePassword': 'الدخول بكلمة المرور',
  'auth.createAccount': 'إنشاء حساب جديد',
  'auth.totp': 'رمز المصادقة',
  'auth.signIn': 'تسجيل الدخول',
  'auth.customer.title': 'دخول العملاء',
  'auth.customer.subtitle': 'استخدم رقم الجوال وكلمة المرور، ورمز التحقق اختياري عند نسيانها.',
  'auth.phone': 'رقم الجوال بصيغة E.164',
  'auth.captcha': 'رمز التحقق البشري',
  'auth.captcha.hint': 'تُصدره أداة التحقق عندما يطلبه الخادم.',
  'auth.requestCode': 'أرسل الرمز',
  'auth.code': 'الرمز لمرة واحدة',
  'auth.verify': 'تحقّق',
  'auth.register': 'إكمال التسجيل',
  'auth.register.title': 'أكمل تسجيلك',
  'auth.register.hint': 'تم التحقق من رقمك. اذكر اسمك لإتمام التسجيل.',
  'auth.firstName': 'الاسم الكامل',
  'auth.locale': 'اللغة المفضّلة',
  'auth.resendIn': 'يمكنك طلب رمز آخر بعد {seconds} ثانية.',
  'auth.codeSent': 'أُرسل الرمز عبر {channel}.',
  'auth.back': 'استخدم رقمًا آخر',
  'auth.signedOut': 'تم تسجيل الخروج.',

  'stepUp.title': 'تأكيد مرفوع الصلاحية',
  'stepUp.body':
    'لحماية حسابك، أكّد هويتك مرة أخرى قبل إتمام هذا الإجراء الحساس. يستمر التأكيد خمس دقائق ويُستخدم لهذا الإجراء فقط.',
  'stepUp.action': 'الإجراء',
  'stepUp.confirm': 'ارفع الصلاحية',
  'stepUp.cancel': 'إلغاء',
  'stepUp.active': 'صلاحية مرفوعة حتى {time}',
  'stepUp.granted': 'تم تأكيد هويتك حتى {time}. لا يمكن استخدام هذا التأكيد إلا للإجراء الحالي.',
  'stepUp.release': 'إلغاء التأكيد',

  'approval.title': 'موافقة ثانية (مبدأ الأربع أعين)',
  'approval.body':
    'هذا الإجراء الحساس يحتاج موافقة مسؤول آخر مخوّل قبل إتمامه.',
  'approval.action': 'الإجراء',
  'approval.act': 'ما تجري الموافقة عليه',
  'approval.aggregate': 'الكائن',
  'approval.proposal': 'الأدوار والنطاقات المقترحة',
  'approval.expectedVersion': 'إصدار السجل',
  'approval.initiator': 'معرّف المستخدم المبادر',
  'approval.initiator.hint':
    'الشخص الذي سينفّذ الإجراء. أنت توافق نيابةً عنه؛ ويرفض الخادم موافقةً يكون المبادر فيها أنت.',
  'approval.approver': 'وافق عليه',
  'approval.reason': 'السبب',
  'approval.create': 'سجّل موافقتي',
  'approval.revoke': 'اسحب موافقتي',
  'approval.hint':
    'أنت تسجّل موافقتك أنت على إجراء شخص آخر. سجّل الدخول بصفتك المُوافِق، وسجّل الموافقة هنا، ثم ينفّذ المبادر العملية.',
  'approval.handoff.hint':
    'أرسل الطلب أدناه إلى حامل ثانٍ لهذه الصلاحية. يفتح مكتب الموافقات في جلسته هو، ويوافق هناك، ثم يعيد إليك الموافقة. لا تُدخل هنا معرّف مستخدم شخص آخر ولا بيانات دخوله إطلاقًا.',
  'approval.request.label': 'طلب التسليم (أرسله إلى المُوافِق)',
  'approval.request.hint': 'يحمل وقائع الإجراء فقط، وليس بيانات اعتماد ولا يمنح شيئًا.',
  'approval.request.copy': 'انسخ الطلب',
  'approval.request.incomplete': 'الإجراء غير محدّد بالكامل بعد — اختر الكائن ودع الشاشة تقرأ إصداره الحالي.',
  'approval.grant.label': 'الموافقة من الطرف الثاني',
  'approval.grant.hint': 'ألصق ما أعاده المُوافِق. هوية المُوافِق تأتي منه، لا منك.',
  'approval.grant.return': 'أعد هذا إلى المبادر.',
  'approval.grant.copy': 'انسخ الموافقة',
  'approval.mismatch': 'أُعطيت هذه الموافقة لإجراء أو كائن أو إصدار مختلف، ولا يمكن صرفها هنا.',
  'approval.stale':
    'تغيّر الإجراء بعد قبول هذه الموافقة، فلم تعد مطابقة له ولن تُرسل. أنشئ طلب تسليم جديدًا للإجراء بصيغته الحالية.',
  'approval.desk.title': 'مكتب الموافقات',
  'approval.desk.body':
    'أنت الطرف الثاني. ما تسجّله هنا يُسجَّل تحت جلستك أنت، برفع صلاحيتك أنت، ويخضع لكل شرط يخضع له الإجراء ذاته.',
  'approval.desk.paste': 'ألصق طلب التسليم الذي أرسله إليك المبادر.',
  'approval.takedown': 'المحتوى الذي يُزال',
  'approval.release': 'الإصدار الذي يُنشر',
  'approval.exception': 'القرار الذي يُبَتّ به',
  'approval.intent': 'بصمة الإجراء (يجب أن تتطابق على الشاشتين)',
  'details.technical': 'تفاصيل تقنية',
  'exception.deals.hint':
    'الصفقات التي ينتقل رصيدها التجاري بهذا التصحيح، مفصولة بفواصل. الترتيب لا يهم — فالشاشتان والخادم يرتّبونها قبل الربط.',
  'exception.resolve.title': 'موافقة ثانية (أربع أعين)',
  'exception.resolve.recorded':
    'يسجّل مسؤولٌ أعلى ثانٍ هذا القرار في جلسته هو، برفع تحققه هو، مرتبطًا بهذا الاستثناء عند الإصدار الذي قرأته، وبالقرار المبيَّن أدناه: المخرج، والوساطة والوسيط المصحَّحان، والمعرض، والصفقات التي ينتقل رصيدها التجاري. يصرف الخادم تلك الموافقة داخل معاملة البتّ نفسها، ويعيد التحقق من صلاحية المُوافِق في تلك اللحظة، ويكتب المُوافِق الذي صرفه في السجل. والمُثير والباتّ والمُوافِق ثلاثة أشخاص متمايزون.',

  'conflict.title': 'غيّر شخص آخر هذا السجل',
  'conflict.body':
    'الإصدار الذي قرأته هذه الشاشة لم يعد الحالي، فرُفض الطلب بدل أن يطمس تغييرهم. أعد قراءة السجل وأكّد الإجراء من جديد — لا تُعاد المحاولة نيابةً عنك.',
  'conflict.reread': 'أعد القراءة وابدأ من جديد',
  'conflict.decided': 'رفض الخادم هذا بسبب الحالة الراهنة',
  'conflict.approvalPending': 'توجد موافقة معلّقة لهذا الإجراء بالفعل',
  'conflict.inProgress': 'ما زال طلب بهذا المفتاح قيد التنفيذ',
  'conflict.keyReuse': 'استُخدم مفتاح عدم التكرار هذا من قبل لطلب مختلف',
  'conflict.collect': 'اجلب النتيجة',

  'guard.audit': 'قبل المتابعة',
  'guard.audit.body': 'سيُحفظ هذا التغيير في سجل النشاط حتى يمكن مراجعته لاحقًا.',
  'guard.typed.label': 'اكتب {value} للتأكيد',
  'guard.typed.mismatch': 'القيمة المكتوبة غير مطابقة.',
  'guard.reason': 'السبب',
  'guard.note': 'ملاحظة (اختيارية)',

  'state.loading': 'جارٍ التحميل',
  'state.empty': 'لا يوجد شيء بعد',
  'state.error': 'تعذّر إتمام الإجراء',
  'state.error.body': 'تعذّر إتمام الإجراء. راجع البيانات وحاول مرة أخرى.',
  'state.diagnostics': 'تفاصيل الدعم الفني',
  'state.retry': 'أعد المحاولة',
  'state.correlation': 'معرّف الارتباط',
  'state.copy': 'نسخ',
  'state.copied': 'تم النسخ',
  'state.unavailable': 'هذه الواجهة غير مخدومة بعد',
  'state.unavailable.body':
    'أجاب الخادم بالحالة {status} على {path}. لا شيء مُختلق هنا: تبقى الشاشة فارغة حتى تصل حزمة {package}.',
  'state.forbidden':
    'هذا السجل خارج نطاقك. يُجيب الخادم ٤٠٤ عن سجل لا يحق لك قراءته، فـ«غير موجود» و«ليس لك» متطابقان تعاقديًا.',
  'state.fields': 'الحقول التي رفضها الخادم',
  'state.detail': 'تفصيل الخادم (تشخيصي)',

  'action.create': 'إنشاء',
  'action.save': 'حفظ',
  'action.cancel': 'إلغاء',
  'action.close': 'إغلاق',
  'action.refresh': 'تحديث',
  'action.open': 'فتح',
  'action.submit': 'إرسال',
  'action.upload': 'رفع',
  'action.more': 'المزيد',
  'action.filter': 'تصفية',
  'action.all': 'الكل',

  'admin.expos.title': 'المعارض',
  'admin.expos.desc':
    'أنشئ المعرض وحدد موعد فتح التسجيل وبدايته ونهايته.',
  'admin.expos.slug': 'المعرّف اللفظي',
  'admin.expos.nameAr': 'الاسم (عربي)',
  'admin.expos.nameEn': 'الاسم (إنجليزي)',
  'admin.expos.timezone': 'المنطقة الزمنية',
  'admin.expos.expectedBooths': 'العدد المتوقع للأجنحة',
  'admin.expos.expectedBoothsHint': 'تقدير تخطيطي فقط. أبعاد تصاميم الأجنحة المسندة فعليًا هي التي تحدد الصالة.',
  'admin.expos.boothCounts': 'الأجنحة الفعلية / المتوقعة',
  'admin.expos.status': 'الحالة',
  'admin.expos.version': 'الإصدار',
  'admin.expos.createHint': 'أضف البيانات الأساسية الآن، ثم أكمِل الجلسات ومخطط الأرضية والمشاركين.',
  'admin.expos.listHint': 'افتح معرضًا لإدارة دورة حياته وجلساته ومخططه ومشاركاته وإصداراته.',
  'admin.expos.empty': 'لا يوجد معرض بعد. أنشئ الأول لفتح مخطط الأرضية والمشاركات.',
  'admin.expo.lifecycle': 'دورة الحياة',
  'admin.expo.lifecycle.desc':
    'انقل المعرض بين الإعلان وفتح التسجيل والتشغيل والانتهاء. يمنع النظام الانتقالات غير الصحيحة تلقائيًا.',
  'admin.expo.schedule.title': 'مواعيد الـEvent',
  'admin.expo.schedule.createHint': 'حدد المواعيد العامة الآن، ويمكنك تعديلها لاحقًا من صفحة الـEvent.',
  'admin.expo.schedule.editHint': 'هذه المواعيد تحرك دورة حياة الـEvent، ويمكن تعديلها قبل مرور مرحلتها.',
  'admin.expo.schedule.registration': 'موعد فتح التسجيل',
  'admin.expo.schedule.start': 'موعد بداية الـEvent',
  'admin.expo.schedule.end': 'موعد نهاية الـEvent',
  'admin.expo.schedule.timezone': 'توقيت الـEvent',
  'admin.expo.schedule.timezoneHint': 'يُقرأ الموعد بتوقيت {timezone}، وليس بتوقيت المتصفح.',
  'admin.expo.schedule.startHint': 'يُغلق التسجيل في هذا الموعد، ثم يبدأ الـEvent عندما تصبح الصالة جاهزة.',
  'admin.expo.schedule.automatic': 'غيّر الحالات تلقائيًا عند حلول المواعيد',
  'admin.expo.schedule.automaticHint':
    'يفتح الخادم التسجيل، ويغلقه عند بداية الـEvent، وينقله إلى مباشر بعد جاهزية الصالة، ثم ينهيه في موعد النهاية.',
  'admin.expo.schedule.error.required': 'هذا الموعد مطلوب.',
  'admin.expo.schedule.error.startOrder': 'يجب أن تبدأ الفعالية بعد فتح التسجيل.',
  'admin.expo.schedule.error.endOrder': 'يجب أن تنتهي الفعالية بعد بدايتها.',
  'admin.expo.schedule.error.timezone': 'هذا الموعد غير صالح في المنطقة الزمنية المختارة.',
  'admin.expo.schedule.error.summary': 'راجع مواعيد الـEvent المظللة قبل الحفظ.',
  'admin.expo.transition': 'تغيير الحالة',
  'admin.expo.transition.notice':
    'ستتغير حالة المعرض إلى «{status}». إذا عدّل شخص آخر المعرض قبلك، سنطلب منك تحديث الصفحة بدلًا من فقدان تعديله.',
  'admin.expo.transition.notReady': 'المرحلة ليست جاهزة بعد',
  'admin.expo.transition.registrationRemedy':
    'حدد موعد فتح التسجيل في مواعيد الـEvent أو انتظر حتى يحين. سيفتحه الخادم تلقائيًا عند تفعيل الانتقال التلقائي.',
  'admin.expo.transition.liveRemedy':
    'يجب أن يحين موعد البداية وأن تحمل جلسة واحدة على الأقل إصدارًا عامًا مفعّلًا. سيعيد الخادم المحاولة تلقائيًا عندما تصبح الصالة جاهزة.',
  'admin.expo.transition.scheduleRemedy': 'راجع مواعيد الـEvent ومتطلبات الجاهزية الظاهرة في الصفحة.',
  'admin.expo.sessions': 'نوافذ الجلسات',
  'admin.expo.sessions.desc':
    'نوافذ غير متداخلة بتوقيت المعرض. التداخل يُرفض بقيد استبعاد، مع إظهار الجلسة المتعارضة.',
  'admin.expo.sessions.starts': 'تبدأ في',
  'admin.expo.sessions.ends': 'تنتهي في',
  'admin.expo.sla': 'إعدادات استجابة فريق المعرض',
  'admin.expo.sla.obligation':
    'التزام التوظيف ١٠٠٪ لكل مقعد مستحق ليس حقلًا في هذه الشاشة: لا يمكن خفضه ولا التنازل عنه ولا جدولته بعيدًا، والمخالفة المسجّلة لا تُعدّل ولا تُحذف من هنا ولا من أي مكان آخر.',
  'admin.expo.recordingPolicy': 'سياسة التسجيل',
  'admin.expo.recordingPolicy.desc':
    'اختر إيقاف تسجيل الجلسات أو محاولة التسجيل عند توافره. يتطلب التغيير تأكيد الهوية وموافقة مسؤول آخر.',
  'admin.expo.recordingPolicy.notice':
    'سيتغير إعداد التسجيل إلى «{policy}»، وسيُحفظ سبب التغيير في سجل النشاط.',
  'expo.status.draft': 'مسودة',
  'expo.status.announced': 'مُعلن',
  'expo.status.registration_open': 'التسجيل مفتوح',
  'expo.status.registration_closed': 'التسجيل مغلق',
  'expo.status.live': 'مباشر',
  'expo.status.ended': 'منتهٍ',
  'expo.status.archived': 'مؤرشف',
  'expo.status.cancelled': 'ملغى',
  'reason.operational': 'حاجة تشغيلية',
  'reason.commercial': 'قرار تجاري',
  'reason.compliance': 'متطلب امتثال',
  'reason.security': 'متطلب أمني',
  'reason.administrative_error': 'تصحيح خطأ إداري',
  'recording.disabled': 'متوقف',
  'recording.best_effort': 'التسجيل عند التوافر',
  'recording.required': 'التسجيل إلزامي',
  'recording.noticeVersion': 'إصدار إشعار التسجيل',
  'recording.justification': 'سبب التغيير',
  'sla.callAcceptTimeout': 'مهلة الرد على المكالمة (ثانية)',
  'sla.missedCallLimit': 'حد المكالمات الفائتة قبل التنبيه (%)',
  'sla.unstaffedGrace': 'مهلة بقاء الجناح دون موظف (ثانية)',
  'sla.seatHoldDuration': 'مدة حجز المقعد مؤقتًا (ثانية)',
  'sla.queueOfferWindow': 'مهلة قبول طلب الانتظار (ثانية)',
  'sla.queueReofferLimit': 'الحد الأقصى لإعادة عرض الطلب',
  'sla.closeGrace': 'مهلة ما بعد الإغلاق (ثانية)',
  'admin.expo.recordingPolicy.gated.short': 'غير متاح حتى اكتمال المراجعة القانونية',
  'admin.expo.recordingPolicy.gated':
    'سيصبح التسجيل الإلزامي متاحًا بعد اعتماد المراجعة القانونية والخصوصية. حتى ذلك الحين اختر «متوقف» أو «التسجيل عند التوافر».',
  'admin.expo.slots': 'مواقع الأجنحة',
  'admin.expo.participations': 'المشاركات',
  'admin.expo.sponsors': 'رعاة المعرض',
  'admin.expo.releases': 'الإصدارات',
  'admin.expo.revenue': 'الاستحقاقات وبنود الإيراد',

  'admin.layouts.title': 'مخططات الصالات',
  'admin.layouts.desc':
    'فهرس مخططات الصالات وتعريفات مواقعها. المخطط المنشور غير قابل للتعديل؛ التغيير ينشئ إصدارًا جديدًا.',
  'admin.layouts.publishedAt': 'تاريخ النشر',
  'admin.layouts.readOnly':
    'السجل للقراءة فقط. يولّد الخادم مخطط الصالة من مشاركات هذا المعرض نفسها ويُنشر من وحدة الأرضية؛ ولا شيء يُركّب يدويًا.',

  'admin.currentHall.title': 'الصالة الحالية',
  'admin.currentHall.activeTitle': 'الصالة الفعّالة',
  'admin.currentHall.desc':
    'توجد صالة واحدة فقط. يقبل العميل هذا المشهد بهذا الإصدار تحديدًا ويرفض ما عداه قبل تحميل أي أصل.',
  'admin.currentHall.sceneKey': 'مفتاح المشهد',
  'admin.currentHall.sceneVersion': 'إصدار المشهد',
  'admin.currentHall.hallId': 'معرّف الصالة',
  'admin.currentHall.layoutVersion': 'إصدار المخطط',
  'admin.currentHall.active': 'فعّالة في هذا المعرض',
  'admin.currentHall.inactive': 'منشورة وغير مرتبطة',
  'admin.currentHall.inactiveHint':
    'يوجد مخطط منشور لهذه الصالة، لكن هذا المعرض غير مرتبط به: سيصل الزائر إلى عالم فارغ. أعد النشر من وحدة الأرضية لربطه.',
  'admin.currentHall.none': 'لم تُنشر أي صالة بعد',
  'admin.currentHall.noneHint':
    'ولّد مخططًا وانشره من وحدة الأرضية أدناه. وإلى أن يحدث ذلك لا يوجد عالم يدخله الزائر.',
  'admin.currentHall.publishedLayout': 'المخطط المنشور، الإصدار {version}',
  'admin.currentHall.openFloor': 'افتح وحدة الأرضية',
  'admin.templates.title': 'فهرس تصاميم الأجنحة',
  'admin.templates.size': 'مقاس الجناح',
  'admin.templates.dimensions': 'الأبعاد المقاسة',
  'admin.templates.seatUnit': 'مقاعد',
  'admin.templates.maxSeats': 'الحد الأقصى للمقاعد',
  'admin.templates.brandingSlots': 'مساحات الهوية البصرية',
  'admin.slots.title': 'مخطط الأرضية',
  'admin.slots.desc':
    'ولّد وانشر مخطط الأرضية المعتمد من تصاميم الأجنحة المسندة فعليًا. العدد المتوقع لا يحد السعة أبدًا.',
  'admin.layoutEngine.title': 'مخطط المعرض الإجرائي',
  'admin.layoutEngine.desc': 'يقيّم الخادم مخططًا وحداتيًا حتميًا من أبعاد الأجنحة المشتراة ومسافات الحركة والفئات والتثبيتات.',
  'admin.layoutEngine.counts': 'أعداد تخطيط الأجنحة',
  'admin.layoutEngine.expected': 'التقدير التخطيطي',
  'admin.layoutEngine.actual': 'المسند فعليًا',
  'admin.layoutEngine.status': 'حالة المخطط',
  'admin.layoutEngine.version': 'إصدار المخطط',
  'admin.layoutEngine.stale': 'هذه المسودة قديمة لأن مشاركة أو تصميم جناح أو مدخلًا تخطيطيًا تغيّر. ولّد مسودة جديدة قبل النشر.',
  'admin.layoutEngine.ceiling': 'صافي ارتفاع السقف (م)',
  'admin.layoutEngine.aisle': 'عرض الممر الرئيسي (م)',
  'admin.layoutEngine.bay': 'عمق الوحدة المعمارية (م)',
  'admin.layoutEngine.run': 'الطول المستهدف الأقصى للمحور (م)',
  'admin.layoutEngine.avenues': 'أقصى عدد للمحاور المتوازية',
  'admin.layoutEngine.generate': 'توليد مسودة جديدة',
  'admin.layoutEngine.publish': 'نشر هذه المسودة نفسها',
  'admin.layoutEngine.preview': 'معاينة المخطط المعتمد',
  'admin.layoutEngine.previewHint': 'تعرض هذه المعاينة لقطة الخادم المحفوظة التي يستهلكها Unity؛ ولا تشغّل محللًا آخر داخل المتصفح.',
  'admin.layoutEngine.empty': 'أسند تصاميم الأجنحة للمشاركات المقبولة ثم ولّد مسودة.',
  'admin.layoutEngine.planAlt': 'مخطط علوي معتمد للمعرض يوضح الممرات ومساحات الأجنحة',
  'admin.layoutEngine.avenueUnit': 'محاور',
  'admin.layoutEngine.publishedSlots': 'مواقع وقت التشغيل المنشورة',
  'admin.layoutEngine.tier': 'فئة الموضع',
  'admin.layoutEngine.minWidth': 'أدنى عرض للصالة (م)',
  'admin.layoutEngine.minDepth': 'أدنى عمق للصالة (م)',
  'admin.layoutEngine.crossAisle': 'عرض الممر العرضي (م)',
  'admin.layoutEngine.margin': 'هامش المحيط (م)',
  'admin.layoutEngine.entrance': 'عمق منطقة المدخل (م)',
  'admin.layoutEngine.endFeature': 'عمق العنصر الطرفي (م)',
  'admin.layoutEngine.aspect': 'نسبة الأبعاد المستهدفة',
  'admin.layoutEngine.validation': 'تحقق المخطط',
  'admin.layoutEngine.validationClean': 'لم يسجّل المحرّك أي أخطاء أو تنبيهات لهذا المخطط.',
  'admin.participations.developer': 'المطور',
  'admin.participations.design': 'تصميم الجناح',
  'admin.participations.chooseDeveloper': 'اختر المطور',
  'admin.participations.chooseDesign': 'اختر تصميم جناح مشتَرى',
  'admin.participations.tier': 'فئة الموضع التجارية',
  'admin.participations.seats': 'المقاعد المشتراة',
  'admin.participations.saveDesign': 'حفظ إسناد تصميم الجناح',
  'admin.participations.slot': 'موقع الجناح',
  'admin.participations.publishBlocked':
    'لا يعدّ الخادم هذا الجناح قابلًا للنشر بعد. تسرد لوحة الجاهزية كل فحص أجراه.',
  'admin.participations.suspendConfirm': 'اكتب معرّف المشاركة لتفعيل الإيقاف',
  'admin.participations.suspendHint':
    'يُخرج الإيقاف جناحًا حيًّا من العالم أمام كل من يقف عنده، ولذلك يُكتب المعرّف أولًا.',
  'admin.verb.accept': 'قبول',
  'admin.verb.withdraw': 'سحب',
  'admin.verb.publish': 'نشر',
  'admin.verb.republish': 'إعادة النشر',
  'admin.verb.suspend': 'إيقاف',
  'admin.verb.take_down': 'إزالة',
  'admin.branding.approve': 'اعتماد',
  'admin.branding.reject': 'رفض',
  'admin.branding.rejectReason': 'رمز سبب الرفض',
  'admin.branding.previewAlt': 'أصل هوية بصرية مُقدّم بوصفه {kind}',
  'media.preview.unavailable':
    'تعذّر تحميل هذه المعاينة. قد لا يكون الأصل صورة يعرضها الخادم، أو أن الطلب أخفق.',

  'readiness.title': 'جاهزية النشر',
  'readiness.desc':
    'يحسبها الخادم بالقواعد نفسها التي ينفّذها انتقال النشر. ولا تستنتج هذه الشاشة منها شيئًا.',
  'readiness.check': 'الفحص',
  'readiness.requirement': 'الإلزام',
  'readiness.detail': 'تفصيل الخادم',
  'readiness.required': 'إلزامي',
  'readiness.optional': 'اختياري',
  'readiness.publishable': 'جاهز للنشر',
  'readiness.blocked': 'غير جاهز للنشر',
  'readiness.state.ok': 'مستوفى',
  'readiness.state.pending': 'بانتظار المراجعة',
  'readiness.state.missing': 'ناقص',
  'readiness.state.rejected': 'مرفوض',
  'readiness.reason': 'رمز السبب',
  'readiness.check.organization_active': 'المنشأة نشطة',
  'readiness.check.template_assigned': 'تصميم الجناح مُسنَد',
  'readiness.check.slot_assigned': 'موقع الجناح مُسنَد',
  'readiness.check.layout_published': 'مخطط الصالة منشور',
  'readiness.check.branding_logo': 'الشعار معتمد',
  'readiness.check.branding_hero_banner': 'اللافتة الرئيسية معتمدة',
  'readiness.check.branding_screen_video': 'فيديو الشاشة معتمد',
  'readiness.check.seats_issued': 'المقاعد مُصدَرة',
  'admin.tier.standard': 'قياسي',
  'admin.tier.premium': 'مميز',
  'admin.tier.signature': 'فاخر',
  'admin.tier.flagship': 'رئيسي',
  'admin.participations.desc':
    'مشاركة واحدة لكل مطوّر في كل معرض، وواحدة لكل موقع، كلاهما بقيد فريد. الاستحقاقات سجلات إعلامية؛ العقود والفوترة تبقى خارج النظام.',
  'admin.branding.title': 'مراجعة الهوية البصرية',
  'admin.branding.desc':
    'الاعتماد يرفع مرحلة البناء إلى BRANDED. تُذكر هذه النتيجة قبل الضغط.',
  'admin.releases.desc':
    'ابنِ إصدارًا غير قابل للتغيير من لقطة مُتحقق منها. ملاحظة البناء هي إسقاط ومسار وقاعدة؛ البناء الفاشل غير قابل للنشر ولا يوجد مرشّح وقت تشغيل يُعوَّل عليه.',
  'admin.releases.activate.desc':
    'التفعيل والتراجع يتطلبان رفع التحقق وموافقة أربع أعين. تساوي المُوافِق مع المبادر مرفوض — وهذه الشاشة تقول ذلك قبل المحاولة.',
  'admin.takedown.title': 'إزالة جناح',
  'admin.takedown.desc':
    'الإزالة نهائية لهذا المعرض: تُفرغ الحافة وتمنع من الأصل وتُبطل إسقاطات الإصدار وتلغي مداخل الطابور المفتوحة وتنهي اللقاءات النشطة في ذلك الجناح. رفع تحقق وأربع أعين ورمز سبب ومرجع نظامي.',
  'admin.orgs.title': 'المنشآت',
  'admin.orgs.desc': 'منشآت الوساطة والتطوير والمنصة، وعضوياتها وشارات التحقق لموظفيها.',
  'admin.memberships.title': 'العضويات والأدوار',
  'admin.memberships.desc':
    'إسناد الدور مقارنة وتبديل: يُرسل إصدار العضوية الذي قرأته في ترويسة If-Match، فيكون التغيير المتزامن تعارضًا لا كتابة فوقه.',
  'admin.sponsors.desc':
    'الرعاية لا تمنح جناحًا ولا مقعدًا ولا موضعًا داخل العالم ولا بيانات عملاء. الإسقاط العام يعيد الرعاة النشطين ضمن فترتهم فقط، مرتبين بترتيب الفئة ثم ترتيب العرض؛ والانتهاء يُحسب في الخادم.',
  'admin.exceptions.title': 'استثناءات العزو',
  'admin.exceptions.desc':
    'لا توجد عملية نزاع ولا تحكيم بين وسيطين هنا. لا ساعة مستوى خدمة ولا نافذة نزاع ولا مرحلة استئناف.',
  'admin.customers.title': 'العملاء',
  'admin.customers.desc':
    'السجل المُقنَّع. كشف الحقول يتطلب رفع تحقق للإجراء customer.pii.reveal مع غرض ومرجع حالة، يشترطهما الخادم ويسجّلهما مع الإفصاح. والتصدير صلاحية منفصلة.',
  'admin.leads.title': 'العملاء المباشرون',
  'admin.leads.desc':
    'كل عميل مباشر بلا مالك على مستوى الوساطة لهذا المعرض. مدة عدم التوجيه هي الفرز الأساسي، لأن العميل غير المخدوم هو الإخفاق الذي وُجدت هذه الشاشة لمنعه.',
  'admin.identity.title': 'دمج الهويات وفصلها',
  'admin.identity.desc':
    'الدمج لا يعيد كتابة تاريخ الاكتساب أبدًا: تُنشأ أسماء مستعارة وسجل حلّ بدلًا من ذلك. رفع تحقق وأربع أعين في العمليتين.',
  'admin.approvals.title': 'الموافقات',
  'admin.approvals.desc':
    'مكتب الطرف الثاني. افتحه في جلستك أنت: ما تسجّله هنا موافقتك على إجراء شخص آخر، برفع صلاحيتك أنت.',
  'reveal.title': 'كشف بيانات التواصل',
  'reveal.desc':
    'الكشف إجراء منفصل ومُدقَّق. يتطلب رفع تحقق للإجراء customer.pii.reveal، ويشترط الخادم غرضًا ومرجع حالة ويسجّلهما مع الإفصاح. وهو يصرف رفع التحقق: منحة واحدة لشخص واحد. وإن ضاع الجواب، فإعادة الطلب بمفتاح الطلب نفسه تعيد الكشف ذاته ولا تُجري كشفًا آخر.',
  'reveal.statement':
    'customer.pii.reveal — إجراء منفصل يتطلب رفع تحقق، وليس استعلامًا. يكتب الخادم صف تدقيق واحدًا، customer.pii.revealed، يسمّي المنفِّذ والعميل، داخل المعاملة نفسها التي تفكّ التشفير — والغرض ومرجع الحالة أدناه يدخلان في ذلك الصف: كلاهما في تفصيله، ومرجع الحالة أيضًا في سببه، إلى جانب صيغة السجل التي كُتبا بها. والمسار يشترطهما.',
  'reveal.statement.spends':
    'وتنفيذه يصرف أيضًا منحة رفع التحقق التي أذنت به، فكشف الشخص التالي يحتاج عامل تحقق جديدًا. أما التصدير فصلاحية مختلفة، بحدود على عدد الصفوف وعلى التكرار.',
  'reveal.purpose': 'الغرض',
  'reveal.case': 'مرجع الحالة',
  'reveal.run': 'اكشف',
  'reveal.audited':
    'سجّل الخادم customer.pii.revealed للعميل {customer} باسمك، داخل المعاملة نفسها التي فكّت التشفير — ومعه غرضك ومرجع الحالة في تفصيل السجل، ومرجع الحالة أيضًا في سببه، وصيغة السجل التي كُتب بها. وقد صرف منحة رفع التحقق التي أذنت به.',
  'reveal.audited.badge': 'كُتب تدقيق الخادم',
  'reveal.audited.nothing': 'لم يُكشف شيء، فلا تدقيق على الخادم',
  'reveal.audited.none': 'لا تدقيق على الخادم (رُفض الكشف)',
  'reveal.audited.unavailable': 'لا شيء ليُكشف، فلا تدقيق على الخادم',
  'reveal.audited.unknown': 'النتيجة غير معلومة — لا جواب يثق به هذا اللسان',
  'reveal.audited.running': 'ما زالت قيد المعالجة — لم تنتهِ المحاولة الأولى',
  'reveal.unavailable': 'لا تحتفظ إدارة العملاء بهذا الرقم',
  'reveal.unavailable.body':
    'رقم هذا السجل التقطه سياق آخر وهو مختوم بمفتاح ذلك السياق، فليس هنا ما تكشفه هذه الشاشة. لم تُصرف منحة رفع التحقق ولم يُسجَّل أي إفصاح. ولن يغيّر جمع عامل تحقق آخر هذه النتيجة — وفي المرة القادمة التي يسجّل فيها هذا الشخص، تكتب إدارة العملاء نسختها ويصبح الكشف ممكنًا.',
  'reveal.nothing': 'لم يُكشف شيء',
  'reveal.nothing.body':
    'أجاب الخادم بالحالة ٢٠٠ ولم يُعِد أي حقل مفكوك التشفير. وهذه ليست إجابة متوقَّعة من هذا المسار — فالسجل الذي لا تحتفظ إدارة العملاء برقمه يُرفض بالرمز customer.reveal_unavailable — لذا لا يُدَّعى هنا شيء: لم يُعرض أي إفصاح، ولا يُفترض أن صف customer.pii.revealed قد كُتب.',
  'reveal.unknown': 'النتيجة غير معلومة هنا',
  'reveal.unknown.body':
    'إمّا أنه لم يعد شيء أصلًا، وإمّا أن الخادم أجاب بعطل، وكلاهما لا يقول ما آل إليه الطلب: قد يكون الكشف سُجِّل فعلًا، وقد لا يكون بلغ الموضع الذي يسجّله. ومنحة رفع التحقق محفوظة — فهذا اللسان لم يصرفها، والخادم لا يصرفها إلا داخل المعاملة التي تفكّ التشفير وتُدقّق.',
  'reveal.unknown.blocked':
    'وإلى أن يُحسم ذلك لا تبدأ هذه اللوحة شيئًا جديدًا: الكشف الجديد يحمل مفتاح طلب جديدًا، والمفتاح الأصلي وحده يستطيع أن يسأل الخادم عمّا فعل. أعد المحاولة لتسأل بذلك المفتاح — فالكشف الذي سُجِّل يُعاد إليك كما سُجِّل تمامًا، إفصاح واحد وصف تدقيق واحد، والذي لم يقع يُجرى الآن.',
  'reveal.unknown.abandon': 'اصرف النظر عن هذه المحاولة',
  'reveal.unknown.abandon.warning':
    'صرف النظر يُسقط الأثر الوحيد الذي يملكه هذا اللسان عن تلك المحاولة: لن يمكن إعادتها من هنا أبدًا. فإن كانت قد تمّت، فالإفصاح وصفّه customer.pii.revealed قائمان على الخادم باسمك، وقد صُرفت المنحة عليه — وسيُرفض الكشف التالي حتى ترفع تحققًا جديدًا.',
  'reveal.running': 'ما زالت المحاولة الأولى قيد التنفيذ',
  'reveal.running.body':
    'أجاب الخادم بأن طلبًا بهذا المفتاح ما زال قيد التنفيذ: الكشف الذي طلبته لم ينتهِ بعد، فلا شيء رُفض ولا شيء حُسم — وقد يكون يُسجَّل بينما تقرأ هذا. ومنحة رفع التحقق محفوظة: هذا اللسان لم يصرفها، والخادم لا يصرفها إلا داخل المعاملة التي تفكّ التشفير وتُدقّق.',
  'reveal.running.blocked':
    'وإلى أن ينتهي لا تبدأ هذه اللوحة شيئًا جديدًا: الكشف الجديد يحمل مفتاح طلب جديدًا، فيكون إفصاحًا ثانيًا عن هذا الشخص إلى جانب الإفصاح الجاري. اجلب النتيجة مرة أخرى بعد قليل — فحين تنتهي المحاولة الأولى يعيد إليك المفتاح نفسه ما سجّلته تمامًا، إفصاحًا واحدًا وصف تدقيق واحدًا.',
  'reveal.running.abandon.warning':
    'صرف النظر يُسقط الأثر الوحيد الذي يملكه هذا اللسان عن محاولة ينفّذها الخادم الآن: لن يمكن جلب نتيجتها من هنا أبدًا. وستكتمل من دونك — فإن أفصحت، فالإفصاح وصفّه customer.pii.revealed قائمان على الخادم باسمك، وقد صُرفت المنحة عليه.',
  'reveal.trail': 'سجل هذه الجلسة المحلي',
  'reveal.trail.local':
    'لا يقدّم هذا الفرع مسار قراءة لسجل التدقيق، فما يلي قائمة هذا اللسان وحده بعمليات الكشف التي طلبها. ليست سجل تدقيق الخادم، ولا تُرسل إلى أي مكان، وتزول بإغلاق اللسان. أما ما تستطيع هذه الشاشة عرضه من سجل الخادم فهو الخط الزمني للإسناد أدناه.',
  'reveal.none': 'لم يُكشف شيء في هذه الجلسة.',

  'brk.invitations.title': 'الدعوات',
  'brk.invitations.desc':
    'دعوات فردية تمر بـ pending ← sent ← opened ← accepted، إضافة إلى expired وrevoked وconflicted. إخفاق الإرسال يُبقي الدعوة pending وقابلة لإعادة المحاولة.',
  'brk.links.title': 'روابط الحملات',
  'brk.links.desc': 'روابط تسجيل بقناة ووسوم UTM. الرابط المُبطل للقراءة فقط.',
  'brk.leads.title': 'العملاء المحتملون',
  'brk.leads.desc':
    'تبقى الهوية مُقنَّعة حتى الإسناد. استثناء العزو المفتوح لا يجمّد شيئًا ولا يمنع الإسناد.',
  'brk.unassigned': 'غير المُسندين فقط',
  'brk.pending.title': 'بانتظار إعادة الإسناد',
  'brk.pending.desc':
    'وسيط هؤلاء العملاء غادر أو أُوقف أو حُذف. المنشأة ما زالت تملكهم وتخدمهم؛ المفتوح هو الإسناد على مستوى الوسيط فقط. لا مؤقّت لهذا الطابور ولا انتهاء.',
  'brk.pending.empty': 'لا يوجد عملاء بانتظار وسيط. هذه هي الحالة السليمة.',
  'brk.bulk.title': 'إعادة إسناد جماعية',
  'brk.bulk.desc':
    'الناجح يُثبَّت والفاشل يُسرد فرديًا مع سببه؛ ولا تُعاد أي محاولة بصمت. تتشارك كل الصفوف معرّف عملية جماعية واحدًا لتُراجع العملية كوحدة.',
  'brk.customer.title': 'العميل',
  'brk.timeline.title': 'الخط الزمني للعزو',
  'brk.timeline.desc':
    'صف الترحيل يعني علاقة ممتدة على مستوى الوسيط بلا نافذة حداثة. اللمسة اللاحقة من وسيط آخر تُسجّل كدليل دون أي أثر على الملكية.',
  'brk.notes.title': 'الملاحظات',
  'brk.exceptions.desc':
    'سجل النظام هو المرجع. المطالبات التجارية المعتادة ليست فئة هنا: الفئات هي administrative_error وidentity_merge_split وfraud وdata_integrity فقط. الرفع من مدير الوساطة وحده؛ والحسم إجراء لمدير المنصة الأعلى.',
  'brk.exceptions.empty': 'لم ترفع هذه الوساطة أي استثناء عزو.',
  'brk.exceptions.resolution':
    'كيف يُحسم استثناؤك: يحسمه مدير منصة أعلى تحت رفع تحقق مرفوع لهذا الإجراء، صارفًا موافقةً ذات استخدام واحد سجّلها مديرٌ ثانٍ من جلسته هو، على هذا الاستثناء بالإصدار الذي قُرئ به. ويستهلك الحسم تلك الموافقة داخل المعاملة نفسها التي تغيّر السجل، ويحفظ المُوافِق الذي سمّته — فالقرار بأربع أعين، ويُظهر الأثر الطرفين معًا لا اسمًا كتبه الحاسم.',

  'dev.booth.title': 'الجناح',
  'dev.booth.desc':
    'الموقع والقالب وعدد المقاعد ولون التمييز وتخطيط الكشك، ضمن الاستحقاق. تغييرات الموقع والقالب إجراءات منصة؛ المطوّر يطلبها.',
  'dev.branding.title': 'الهوية البصرية',
  'dev.branding.desc':
    'رفع الهوية البصرية يقدّم مرحلة البناء. بلا أصول يُعرض الجناح في مرحلة SCAFFOLD/STRUCTURE. الاعتماد إجراء منصة.',
  'dev.branding.quota': 'الاستحقاق: استُخدم {used} من {limit} أصلًا لهذه المشاركة.',
  'dev.branding.kind.logo': 'الشعار',
  'dev.branding.kind.hero_banner': 'اللافتة الرئيسية',
  'dev.branding.kind.screen_video': 'فيديو الشاشة',
  'dev.branding.none': 'لم يُرفع شيء لهذه الخانة بعد.',
  'dev.branding.locale': 'لغة هذا الأصل',
  'dev.branding.file': 'اختر ملفًا',
  'dev.branding.noFile': 'اختر ملفًا قبل الرفع.',
  'dev.branding.uploaded': 'تم الرفع والتقديم للمراجعة في خطوة واحدة.',
  'dev.branding.previewAlt': '{kind} المرفوع',
  'dev.branding.noStill': 'لا توجد معاينة ثابتة للفيديو هنا؛ يشغّل المراجع الملف نفسه.',
  'dev.branding.atQuota': 'استهلك هذا الجناح كل خانات الهوية البصرية التي يسمح بها تصميمه.',
  'dev.booth.noTemplate': 'لا يوجد تصميم جناح مُسنَد',
  'dev.booth.noTemplateHint':
    'لم يُسند فريق المعرض تصميم جناح لهذه المشاركة بعد، ولذلك لم تتحدد استحقاقات المقاعد والهوية البصرية.',
  'dev.readiness.desc':
    'ما يراه فحص النشر لدى فريق المعرض. لا يمكنك نشر جناحك بنفسك؛ وهذا ما ينقصه.',
  'dev.company.title': 'ملف المنشأة',
  'dev.company.desc':
    'منشأتك كما يراها الزوار وفريق المعرض. الهوية القانونية وحالة التوثيق قرار المنصة؛ وما عداهما تكتبه أنت.',
  'dev.company.identity': 'الهوية القانونية',
  'dev.company.identityHint': 'للقراءة فقط. اطلب من فريق المعرض تصحيح أي خطأ هنا.',
  'dev.company.legalNameAr': 'الاسم القانوني (بالعربية)',
  'dev.company.legalNameEn': 'الاسم القانوني (بالإنجليزية)',
  'dev.company.displayName': 'الاسم المعروض',
  'dev.company.verification': 'حالة التوثيق',
  'dev.company.country': 'الدولة',
  'dev.company.profile': 'الملف العام',
  'dev.company.profileHint':
    'يُحفظ مقارنةً بالإصدار المقروء أعلاه، فتحرير زميل في الوقت نفسه تعارض لا استبدال صامت.',
  'dev.company.website': 'الموقع الإلكتروني',
  'dev.company.websiteHint': 'عنوان https كامل، أو اتركه فارغًا.',
  'dev.company.websiteError': 'يجب أن يكون الموقع عنوان https كاملًا، أو فارغًا.',
  'dev.company.aboutAr': 'نبذة (بالعربية)',
  'dev.company.aboutEn': 'نبذة (بالإنجليزية)',
  'dev.company.aboutError': 'بحد أقصى 2000 حرف.',
  'dev.company.saved': 'حُفظ الملف.',
  'dev.projects.title': 'المشاريع',
  'dev.projects.desc': 'المشروع المعتمد للقراءة فقط إلا عبر تقديم جديد.',
  'dev.unitTypes.title': 'أنواع الوحدات',
  'dev.units.import': 'استيراد الوحدات',
  'dev.units.import.desc':
    'CSV أو JSON. كل صف يُبلَّغ عنه على حدة: الدفعة لا تُرفض كاملة بسبب صف واحد.',
  'dev.price.title': 'سعر القائمة',
  'dev.offers.title': 'العروض',
  'dev.offers.desc':
    'النطاق والنوع والقيمة والصلاحية والحصة ودرجة النشر. نشر التعريف ليس صلاحية مطالبة.',
  'dev.holds.title': 'الحجوزات المؤقتة',
  'dev.holds.desc':
    'يُنشأ الحجز وفق مدة المطوّر نفسه ويُلتقط في لقطة؛ فتغيير السياسة لاحقًا لا يحرّك حجزًا قائمًا. والحجز المنتهي لا يُجدَّد بصمت.',
  'dev.holds.conflict':
    'الحجز الذي تحمله هذه الشاشة قديم — فقد مُدّد أو أُفرج عنه أو انتهى في مكان آخر. ولا تستطيع هذه الشاشة إعادة قراءته لك: واجهة المطوّر تقدّم الإنشاء والتمديد والإفراج فقط، ولا تقدّم أي مسار قراءة، فلا شيء هنا يستطيع إظهار حالته الراهنة. امسحه أدناه وأنشئ حجزًا جديدًا إن كانت الوحدة ما زالت متاحة.',
  'dev.holds.forget': 'امسح هذا الحجز القديم من الشاشة',
  'dev.holdPolicy.title': 'سياسة الحجز',
  'dev.holdPolicy.desc':
    'مدة الحجز بالساعات وعدد التمديدات المسموحة. السجل موجود دائمًا — يُنشأ بالقيم الافتراضية عند التسجيل — فلا توجد حالة «غير مهيّأ». التغيير يسري على الحجوزات اللاحقة فقط.',
  'dev.announce.title': 'إعلان المشاريع',
  'dev.announce.desc': 'أي المشاريع تظهر على الجناح وبأي ترتيب. يتطلب مشروعًا منشورًا واحدًا على الأقل.',
  'dev.duty.title': 'لوحة المناوبة',
  'dev.duty.desc':
    'الخروج من المناوبة بلا مناوب قادم متاح ويُعرض على حقيقته: من تلك اللحظة يصبح المقعد بلا تغطية وتُفتح فترة مخالفة لمستوى الخدمة.',
  'dev.duty.checkIn': 'بدء المناوبة',
  'dev.duty.handover': 'تسليم',
  'dev.duty.checkOut': 'إنهاء المناوبة',
  'dev.duty.offDuty': 'أنت خارج المناوبة.',
  'dev.queue.title': 'مراقب الطابور',
  'dev.queue.desc':
    'الترتيب هو الموعد المحجوز ثم كبار العملاء ثم الحضور المباشر بالأسبقية، ويأتي من الخادم. تُعرض فئة الأولوية؛ والرتبة غير قابلة للتحرير أبدًا.',
  'dev.queue.callNext': 'نادِ التالي',
  'dev.queue.empty': 'لا أحد ينتظر.',
  'dev.queue.noSeat': 'لا تملك مقعدًا في المناوبة، فالطابور معروض دون تقديم أي زر نداء.',

  'cus.expos.title': 'المعارض',
  'cus.enroll': 'سجّل في هذا المعرض',
  'cus.enrollments.title': 'تسجيلاتي',
  'cus.consents.title': 'مركز الخصوصية',
  'cus.consents.desc':
    'قرارات الموافقة مع إصدارات الإشعار. سحب موافقة التحليلات يوقف الدرجات والتنبيهات فورًا، قبل تشغيل مهام الحذف.',
  'cus.attribution.title': 'من يخدمني',
  'cus.attribution.desc':
    'تنتقل العلاقة من معرض إلى آخر ما دام الوسيط عاملًا في تلك المنشأة. ولا توجد عملية نزاع تدخلها.',
  'cus.attribution.none': 'يخدمك فريق الأرضية لدينا مباشرة.',
  'cus.blocks.title': 'جهات محظورة',
  'cus.sessions.title': 'الجلسات والأجهزة',
  'cus.sessions.desc': 'إبطال الجلسة الحالية يسجّل خروجك. هذا يُذكر أولًا.',
  'cus.sessions.revokeOthers': 'أبطل كل الجلسات الأخرى',
  'cus.shortlist.title': 'المفضلة',

  'table.identifier': 'المعرّف',
  'table.created': 'أُنشئ',
  'table.status': 'الحالة',
  'table.actions': 'إجراءات',
  'table.name': 'الاسم',
  'table.kind': 'النوع',
  'table.code': 'الرمز',
  'table.rank': 'الترتيب',
  'table.none': '—',
  'status.active': 'نشط',
  'status.retired': 'متوقف',
  'status.draft': 'مسودة',
  'status.published': 'منشور',
  'action.publish': 'نشر',
};

export const dictionaries = { ar, en: en as unknown as Dictionary } satisfies Record<string, Dictionary>;

export type Locale = keyof typeof dictionaries;
